import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  CheckCircle,
  XCircle,
  ExternalLink,
  Search,
  Loader2,
  RefreshCw,
  Plus,
  Crown,
  CheckSquare,
  Square,
  MinusSquare,
  Wallet,
  AlertCircle,
  Calendar,
  Zap,
  User,
  ArrowRight,
  MoreVertical,
  Edit3,
  ChevronDown,
  ChevronUp,
  Package,
  DollarSign,
  Coins,
  Tag,
} from 'lucide-react'
import { cn, shortenAddress, getExplorerUrl } from '@/lib/utils'
import { useAdminQueue, BridgeRequest } from '@/hooks/useAdminQueue'
import { api, UserDetailsResponse, RejectionTemplate } from '@/lib/api'
import { useAccount, useSwitchChain } from 'wagmi'
import { useSendUSDT } from '@/hooks/useSendUSDT'
import { TREASURY_WALLET_BSC, BSC_CHAIN_ID } from '@/constants/addresses'
import { SchedulePaymentModal } from '@/components/SchedulePaymentModal'
import { BulkPaymentModal } from '@/components/BulkPaymentModal'
import toast from 'react-hot-toast'

const statusLabels: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  COMPLETED: 'Completado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Cancelado',
  SCHEDULED: 'Programado',
  FAILED: 'Fallido',
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-500/20 text-yellow-400',
  APPROVED: 'bg-blue-500/20 text-blue-400',
  COMPLETED: 'bg-accent-500/20 text-accent-400',
  REJECTED: 'bg-red-500/20 text-red-400',
  CANCELLED: 'bg-dark-500/20 text-dark-400',
  SCHEDULED: 'bg-purple-500/20 text-purple-400',
  FAILED: 'bg-red-500/20 text-red-400',
}

export function QueuePage() {
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [rejectModalId, setRejectModalId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [showReasonToUser, setShowReasonToUser] = useState(false)
  const [rejectionTemplates, setRejectionTemplates] = useState<RejectionTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('')
  const [confirmModalId, setConfirmModalId] = useState<string | null>(null)
  const [destTxHash, setDestTxHash] = useState('')
  const [showManualAddModal, setShowManualAddModal] = useState(false)
  const [manualTxHash, setManualTxHash] = useState('')
  const [isManualAdding, setIsManualAdding] = useState(false)

  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false)
  const [bulkRejectReason, setBulkRejectReason] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)

  // Payment modal
  const [paymentModalRequest, setPaymentModalRequest] = useState<BridgeRequest | null>(null)
  const [paymentCheck, setPaymentCheck] = useState<any>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [isExecutingPayment, setIsExecutingPayment] = useState(false)
  const [paymentMode, setPaymentMode] = useState<'manual' | 'automatic'>('automatic')

  // Schedule modal
  const [scheduleModalRequest, setScheduleModalRequest] = useState<BridgeRequest | null>(null)

  // Bulk payment modal
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false)

  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

  // Accordion - User details state
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  const [userDetails, setUserDetails] = useState<UserDetailsResponse | null>(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)


  // Alias modal state
  const [aliasModalRequest, setAliasModalRequest] = useState<BridgeRequest | null>(null)
  const [aliasValue, setAliasValue] = useState('')
  const [isSavingAlias, setIsSavingAlias] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Manual payment state
  const [manualPaymentStep, setManualPaymentStep] = useState<'ready' | 'sending' | 'verifying' | 'completing'>('ready')

  // Ref to keep track of current request during async operations
  const pendingPaymentRef = useRef<{ requestId: string; bscTxHash: string } | null>(null)

  // Wagmi hooks for manual payment
  const { address, chainId } = useAccount()
  const { switchChainAsync } = useSwitchChain()
  const {
    sendUSDT,
    hash: sendUsdtHash,
    isPending: isSendingUsdt,
    isConfirming: isConfirmingUsdt,
    isSuccess: isSendUsdtSuccess,
    reset: resetSendUsdt,
  } = useSendUSDT()

  // Check if connected wallet is treasury
  const isConnectedTreasury = address?.toLowerCase() === TREASURY_WALLET_BSC.toLowerCase()
  const isOnBSC = chainId === BSC_CHAIN_ID

  // useAdminQueue must be declared before callbacks that use refetch
  const {
    requests,
    stats,
    isLoading,
    refetch,
    approveRequest,
    isApproving,
    rejectRequest,
    isRejecting,
    confirmRelease,
    isConfirming,
  } = useAdminQueue({
    status: filter !== 'all' ? filter : undefined,
    search: search || undefined,
    limit: 100,
  })

  // Helper to save/load/clear pending payment from localStorage
  const PENDING_PAYMENT_KEY = 'w2usdt_pending_manual_payment'

  const savePendingPayment = useCallback((requestId: string, bscTxHash: string) => {
    const data = { requestId, bscTxHash, timestamp: Date.now() }
    localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify(data))
    pendingPaymentRef.current = { requestId, bscTxHash }
  }, [])

  const loadPendingPayment = useCallback((): { requestId: string; bscTxHash: string } | null => {
    try {
      const stored = localStorage.getItem(PENDING_PAYMENT_KEY)
      if (!stored) return null
      const data = JSON.parse(stored)
      // Expire after 1 hour
      if (Date.now() - data.timestamp > 60 * 60 * 1000) {
        localStorage.removeItem(PENDING_PAYMENT_KEY)
        return null
      }
      return { requestId: data.requestId, bscTxHash: data.bscTxHash }
    } catch {
      return null
    }
  }, [])

  const clearPendingPayment = useCallback(() => {
    localStorage.removeItem(PENDING_PAYMENT_KEY)
    pendingPaymentRef.current = null
  }, [])

  // Complete payment on contract - works even if modal is closed
  const completePaymentOnContract = useCallback(async (requestId: string, bscTxHash: string) => {
    try {
      console.log(`[ManualPayment] Completing payment for request ${requestId} with TX ${bscTxHash}`)
      const result = await api.completeOnContract(requestId, bscTxHash)
      if (result.success) {
        toast.success('Pago completado correctamente!')
        clearPendingPayment()
        refetch()
        return true
      } else {
        toast.error('Error al completar en contrato')
        return false
      }
    } catch (error: any) {
      console.error('[ManualPayment] Error completing on contract:', error)
      toast.error(error.message || 'Error al completar en contrato')
      return false
    }
  }, [clearPendingPayment, refetch])

  // Effect to handle manual payment flow - uses ref to avoid stale closure issues
  useEffect(() => {
    if (isSendUsdtSuccess && sendUsdtHash && manualPaymentStep === 'sending') {
      setManualPaymentStep('verifying')

      // Get requestId from ref or current modal state
      const requestId = pendingPaymentRef.current?.requestId || paymentModalRequest?.id

      if (requestId) {
        // Save to localStorage immediately in case user closes modal
        savePendingPayment(requestId, sendUsdtHash)

        setManualPaymentStep('completing')
        completePaymentOnContract(requestId, sendUsdtHash).then((success) => {
          if (success) {
            setManualPaymentStep('ready')
            setPaymentModalRequest(null)
            setPaymentCheck(null)
            resetSendUsdt()
          } else {
            // Keep modal open so user can see the error and retry
            setManualPaymentStep('ready')
          }
        })
      } else {
        console.error('[ManualPayment] No requestId available for completing payment')
        toast.error('Error: No se pudo identificar la solicitud')
        setManualPaymentStep('ready')
      }
    }
  }, [isSendUsdtSuccess, sendUsdtHash, manualPaymentStep, savePendingPayment, completePaymentOnContract, resetSendUsdt])

  // Effect to recover pending payments on page load
  useEffect(() => {
    const pending = loadPendingPayment()
    if (pending) {
      console.log('[ManualPayment] Found pending payment to complete:', pending)
      toast.loading('Completando pago pendiente...', { id: 'pending-payment' })
      completePaymentOnContract(pending.requestId, pending.bscTxHash).then((success) => {
        if (success) {
          toast.success('Pago pendiente completado!', { id: 'pending-payment' })
        } else {
          toast.error('Error al completar pago pendiente. Usa "Confirmar Liberación Manual" para registrarlo.', { id: 'pending-payment' })
          // Don't clear - let user retry or manually confirm
        }
      })
    }
  }, [loadPendingPayment, completePaymentOnContract])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null)
    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [openDropdownId])

  // Load rejection templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await api.getRejectionTemplates()
        setRejectionTemplates(templates)
        // Pre-select default template if exists
        const defaultTemplate = templates.find(t => t.isDefault)
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.id)
          setRejectReason(defaultTemplate.content)
        }
      } catch (error) {
        console.error('Error loading rejection templates:', error)
      }
    }
    loadTemplates()
  }, [])


  // Calcular sumatorios de seleccionados
  const selectedSummary = useMemo(() => {
    const selected = requests.filter((r: BridgeRequest) => selectedIds.has(r.id))

    const pendingTotal = selected
      .filter((r: BridgeRequest) => r.status === 'PENDING')
      .reduce((sum: number, r: BridgeRequest) => sum + parseFloat(r.formattedAmountIn || '0'), 0)

    const cancelledTotal = selected
      .filter((r: BridgeRequest) => r.status === 'CANCELLED')
      .reduce((sum: number, r: BridgeRequest) => sum + parseFloat(r.formattedAmountIn || '0'), 0)

    const allTotal = selected.reduce((sum: number, r: BridgeRequest) => sum + parseFloat(r.formattedAmountIn || '0'), 0)

    return {
      count: selected.length,
      pendingCount: selected.filter((r: BridgeRequest) => r.status === 'PENDING').length,
      cancelledCount: selected.filter((r: BridgeRequest) => r.status === 'CANCELLED').length,
      pendingTotal,
      cancelledTotal,
      allTotal,
    }
  }, [requests, selectedIds])

  // Handlers de selección
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const toggleSelectAll = () => {
    // Calculate paginatedRequests here for the current state
    const currentPageRequests = requests.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    const allPageSelected = currentPageRequests.every((r: BridgeRequest) => selectedIds.has(r.id))

    if (allPageSelected) {
      // Deselect all from current page
      const newSelected = new Set(selectedIds)
      currentPageRequests.forEach((r: BridgeRequest) => newSelected.delete(r.id))
      setSelectedIds(newSelected)
    } else {
      // Select all from current page
      const newSelected = new Set(selectedIds)
      currentPageRequests.forEach((r: BridgeRequest) => newSelected.add(r.id))
      setSelectedIds(newSelected)
    }
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
  }

  // Acciones masivas
  const handleBulkReject = async () => {
    if (!bulkRejectReason.trim()) {
      toast.error('Ingresa una razón para el rechazo')
      return
    }

    const pendingIds = Array.from(selectedIds).filter(id => {
      const req = requests.find((r: BridgeRequest) => r.id === id)
      return req?.status === 'PENDING'
    })

    if (pendingIds.length === 0) {
      toast.error('No hay solicitudes pendientes seleccionadas')
      return
    }

    setIsBulkProcessing(true)
    try {
      const result = await api.bulkReject(pendingIds, bulkRejectReason)
      toast.success(`${result.rejected} solicitudes rechazadas`)
      if (result.failed > 0) {
        toast.error(`${result.failed} fallaron`)
      }
      setShowBulkRejectModal(false)
      setBulkRejectReason('')
      clearSelection()
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al rechazar')
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleApprove = (id: string) => {
    approveRequest(id)
  }

  const handleReject = () => {
    if (!rejectModalId || !rejectReason.trim()) {
      toast.error('Ingresa una razón para el rechazo')
      return
    }
    rejectRequest({ id: rejectModalId, reason: rejectReason, showReasonToUser })
    setRejectModalId(null)
    setRejectReason('')
    setShowReasonToUser(false)
    // Reset to default template
    const defaultTemplate = rejectionTemplates.find(t => t.isDefault)
    if (defaultTemplate) {
      setSelectedTemplateId(defaultTemplate.id)
    } else {
      setSelectedTemplateId('')
    }
  }

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId)
    if (templateId) {
      const template = rejectionTemplates.find(t => t.id === templateId)
      if (template) {
        setRejectReason(template.content)
      }
    }
  }

  const handleConfirmRelease = () => {
    if (!confirmModalId || !destTxHash.trim()) {
      toast.error('Ingresa el hash de la transacción BSC')
      return
    }
    if (!destTxHash.startsWith('0x') || destTxHash.length !== 66) {
      toast.error('Hash de transacción inválido')
      return
    }
    confirmRelease({ id: confirmModalId, destTxHash })
    setConfirmModalId(null)
    setDestTxHash('')
  }

  const handleManualAdd = async () => {
    if (!manualTxHash.trim()) {
      toast.error('Ingresa el hash de la transacción')
      return
    }
    if (!manualTxHash.startsWith('0x') || manualTxHash.length !== 66) {
      toast.error('Hash de transacción inválido')
      return
    }
    setIsManualAdding(true)
    try {
      await api.manualAddBridgeRequest(manualTxHash)
      toast.success('Solicitud añadida correctamente')
      setShowManualAddModal(false)
      setManualTxHash('')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al añadir la solicitud')
    } finally {
      setIsManualAdding(false)
    }
  }

  // Payment handlers
  const openPaymentModal = async (request: BridgeRequest) => {
    setPaymentModalRequest(request)
    setIsCheckingPayment(true)
    setPaymentCheck(null)
    try {
      const check = await api.checkPayment(request.id)
      setPaymentCheck(check)
    } catch (error: any) {
      toast.error(error.message || 'Error al verificar pago')
      setPaymentModalRequest(null)
    } finally {
      setIsCheckingPayment(false)
    }
  }

  const handleExecutePayment = async () => {
    if (!paymentModalRequest) return
    // Prevent double-click
    if (isExecutingPayment) return
    setIsExecutingPayment(true)
    try {
      const result = await api.executePayment(paymentModalRequest.id)
      toast.success(`Pago completado! BSC TX: ${result.bscTxHash.slice(0, 10)}...`)
      setPaymentModalRequest(null)
      setPaymentCheck(null)
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al ejecutar pago')
    } finally {
      setIsExecutingPayment(false)
    }
  }

  const closePaymentModal = () => {
    // Don't allow closing if payment is in progress
    if (isExecutingPayment) {
      toast.error('Espera a que termine el pago automático')
      return
    }
    if (manualPaymentStep !== 'ready') {
      toast.error('Hay una transacción en proceso. Si cierras, se completará en segundo plano.')
      // Allow closing but the pending payment will be recovered on page load
    }
    setPaymentModalRequest(null)
    setPaymentCheck(null)
    setPaymentMode('automatic')
    setManualPaymentStep('ready')
    resetSendUsdt()
  }

  // Manual payment handler
  const handleManualPayment = async () => {
    if (!paymentModalRequest) return

    // Prevent double-click: set state IMMEDIATELY before any async operation
    if (manualPaymentStep !== 'ready') return
    setManualPaymentStep('sending')

    // Store requestId in ref BEFORE sending TX - this ensures we have it even if modal closes
    pendingPaymentRef.current = { requestId: paymentModalRequest.id, bscTxHash: '' }
    console.log(`[ManualPayment] Starting payment for request ${paymentModalRequest.id}`)

    try {
      // Switch to BSC if needed
      if (chainId !== BSC_CHAIN_ID) {
        await switchChainAsync({ chainId: BSC_CHAIN_ID })
      }

      await sendUSDT(paymentModalRequest.toAddress, paymentModalRequest.formattedAmountOut)
    } catch (error: any) {
      toast.error(error.message || 'Error al enviar USDT')
      setManualPaymentStep('ready')
      pendingPaymentRef.current = null
    }
  }

  // Get selected requests for bulk payment
  const selectedRequests = useMemo(() => {
    return requests.filter((r: BridgeRequest) =>
      selectedIds.has(r.id) && (r.status === 'PENDING' || r.status === 'APPROVED')
    )
  }, [requests, selectedIds])

  // Handle row click to expand/collapse accordion
  const handleRowClick = async (request: BridgeRequest) => {
    if (expandedRowId === request.id) {
      // Collapse
      setExpandedRowId(null)
      setUserDetails(null)
    } else {
      // Expand and fetch details
      setExpandedRowId(request.id)
      setIsLoadingDetails(true)
      setUserDetails(null)
      try {
        const details = await api.getUserDetails(request.fromAddress)
        setUserDetails(details)
      } catch (error) {
        console.error('Error fetching user details:', error)
        toast.error('Error al cargar detalles del usuario')
      } finally {
        setIsLoadingDetails(false)
      }
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Save alias handler
  const handleSaveAlias = async () => {
    if (!aliasModalRequest) return
    setIsSavingAlias(true)
    try {
      await api.setWalletAlias(aliasModalRequest.fromAddress, aliasValue.trim())
      toast.success('Alias guardado')
      setAliasModalRequest(null)
      setAliasValue('')
      refetch()
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar alias')
    } finally {
      setIsSavingAlias(false)
    }
  }

  // Pagination
  const totalPages = Math.ceil(requests.length / pageSize)
  const paginatedRequests = requests.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const isAllSelected = paginatedRequests.length > 0 && paginatedRequests.every((r: BridgeRequest) => selectedIds.has(r.id))
  const isSomeSelected = paginatedRequests.some((r: BridgeRequest) => selectedIds.has(r.id)) && !isAllSelected

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cola de Solicitudes</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-dark-400">
            <span>Pendientes: {stats?.pending || 0}</span>
            <span>•</span>
            <span>Aprobados: {stats?.approved || 0}</span>
          </div>
          <button
            onClick={() => setShowManualAddModal(true)}
            className="btn-primary btn-sm flex items-center gap-1"
            title="Añadir manualmente"
          >
            <Plus className="w-4 h-4" />
            Añadir
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg hover:bg-dark-800 transition-colors text-dark-400 hover:text-white"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="text"
            placeholder="Buscar por dirección o hash..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 text-sm w-full"
          />
        </div>
        <div className="flex gap-3 items-center">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input w-36 text-sm"
          >
            <option value="all">Todos</option>
            <option value="PENDING">Pendientes</option>
            <option value="APPROVED">Aprobados</option>
            <option value="COMPLETED">Completados</option>
            <option value="REJECTED">Rechazados</option>
            <option value="CANCELLED">Cancelados</option>
          </select>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value))
              setCurrentPage(1)
            }}
            className="input w-24 text-sm"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
          </select>
        </div>
      </div>

      {/* Barra de selección y acciones masivas */}
      {selectedIds.size > 0 && (
        <div className="card p-3 bg-uni-500/10 border border-uni-500/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-uni-400">
                {selectedIds.size} seleccionado{selectedIds.size !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-yellow-400">
                  Pendientes: {selectedSummary.pendingCount} ({selectedSummary.pendingTotal.toFixed(2)} W2USDT)
                </span>
                <span className="text-dark-400">
                  Cancelados: {selectedSummary.cancelledCount} ({selectedSummary.cancelledTotal.toFixed(2)} W2USDT)
                </span>
                <span className="text-white font-medium">
                  Total: {selectedSummary.allTotal.toFixed(2)} W2USDT
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBulkPaymentModal(true)}
                disabled={isBulkProcessing || selectedRequests.length === 0}
                className="btn-sm bg-green-500/20 text-green-400 hover:bg-green-500/30 disabled:opacity-50 flex items-center gap-1"
              >
                <Zap className="w-4 h-4" />
                Pagar ({selectedRequests.length})
              </button>
              <button
                onClick={() => {
                  // Use the first selected request as base for schedule modal
                  if (selectedRequests.length > 0) {
                    setScheduleModalRequest(selectedRequests[0])
                  }
                }}
                disabled={isBulkProcessing || selectedRequests.length === 0}
                className="btn-sm bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 disabled:opacity-50 flex items-center gap-1"
              >
                <Calendar className="w-4 h-4" />
                Programar ({selectedRequests.length})
              </button>
              <button
                onClick={() => setShowBulkRejectModal(true)}
                disabled={isBulkProcessing || selectedSummary.pendingCount === 0}
                className="btn-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-1"
              >
                <XCircle className="w-4 h-4" />
                Rechazar ({selectedSummary.pendingCount})
              </button>
              <button
                onClick={clearSelection}
                className="btn-sm bg-dark-700 text-dark-300 hover:bg-dark-600"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && requests.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
        </div>
      )}

      {/* Queue Table */}
      {(!isLoading || requests.length > 0) && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-dark-800 text-dark-400">
                <tr>
                  <th className="px-3 py-3 text-left w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="text-dark-400 hover:text-white"
                    >
                      {isAllSelected ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : isSomeSelected ? (
                        <MinusSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-left">Usuario</th>
                  <th className="px-4 py-3 text-left">Alias</th>
                  <th className="px-4 py-3 text-left">Username</th>
                  <th className="px-4 py-3 text-center">VIP</th>
                  <th className="px-4 py-3 text-left">Destino BSC</th>
                  <th className="px-4 py-3 text-right">Cantidad</th>
                  <th className="px-4 py-3 text-right">Recibe</th>
                  <th className="px-4 py-3 text-left">TX</th>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-800">
                {paginatedRequests.map((request: BridgeRequest) => (
                  <>
                  <tr
                    key={request.id}
                    onClick={() => handleRowClick(request)}
                    className={cn(
                      'hover:bg-dark-800/50 cursor-pointer transition-colors',
                      selectedIds.has(request.id) && 'bg-uni-500/5',
                      expandedRowId === request.id && 'bg-dark-800/30'
                    )}
                  >
                    <td className="px-3 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleSelect(request.id)
                        }}
                        className="text-dark-400 hover:text-white"
                      >
                        {selectedIds.has(request.id) ? (
                          <CheckSquare className="w-4 h-4 text-uni-400" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'badge text-xs',
                          statusColors[request.status] || statusColors.PENDING
                        )}
                      >
                        {statusLabels[request.status] || request.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className="flex flex-col">
                          {/* Nombre del usuario */}
                          {request.allMongoUsers && request.allMongoUsers.length > 0 && request.allMongoUsers[0].name && (
                            <span className="text-white text-xs font-medium">
                              {request.allMongoUsers[0].name}
                            </span>
                          )}
                          {/* Wallet address siempre visible */}
                          <span className="font-mono text-[10px] text-dark-400">
                            {shortenAddress(request.fromAddress, 6)}
                          </span>
                        </div>
                        {/* Enlace a Wer2Scan */}
                        <a
                          href={`https://wer2scan.com/uni2/address/${request.fromAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-1 rounded-md bg-dark-700 text-dark-400 hover:bg-dark-600 hover:text-white transition-colors"
                          title="Ver en Wer2Scan"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-cyan-400 truncate max-w-[80px]" title={request.walletAlias || ''}>
                          {request.walletAlias || '-'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setAliasModalRequest(request)
                            setAliasValue(request.walletAlias || '')
                          }}
                          className="p-1 rounded-md text-dark-500 hover:text-white hover:bg-dark-700 transition-colors"
                          title="Editar alias"
                        >
                          <Tag className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-dark-300">
                        {(request.allMongoUsers && request.allMongoUsers[0]?.username) || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(request.allMongoUsers && request.allMongoUsers[0]?.isVip) ? (
                        <Crown className="w-4 h-4 text-yellow-400 mx-auto" />
                      ) : (
                        <span className="text-dark-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs">
                        {shortenAddress(request.toAddress, 6)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {parseFloat(request.formattedAmountIn).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-accent-400 font-mono">
                      {parseFloat(request.formattedAmountOut).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={getExplorerUrl(111888, 'tx', request.sourceTxHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-uni-400 hover:underline flex items-center gap-1 text-xs"
                      >
                        Ver <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-xs text-dark-400">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Expand indicator */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRowClick(request)
                          }}
                          className="p-1.5 rounded-lg text-dark-400 hover:text-white hover:bg-dark-700"
                          title="Ver detalles"
                        >
                          {expandedRowId === request.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {(request.status === 'PENDING' || request.status === 'APPROVED') && (
                          <>
                            {/* Pago automático - fuera del dropdown */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                openPaymentModal(request)
                              }}
                              className="p-1.5 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              title="Pago Automático"
                            >
                              <Zap className="w-4 h-4" />
                            </button>

                            {/* Programar - fuera del dropdown */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setScheduleModalRequest(request)
                              }}
                              className="p-1.5 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                              title="Programar Pago"
                            >
                              <Calendar className="w-4 h-4" />
                            </button>

                            {/* Menú desplegable para otras acciones */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setOpenDropdownId(openDropdownId === request.id ? null : request.id)
                                }}
                                className="p-1.5 rounded-lg bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white"
                                title="Más acciones"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              {openDropdownId === request.id && (
                                <div
                                  className="absolute right-0 top-full mt-1 w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50 py-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* Flujo Manual */}
                                  <div className="px-3 py-1 text-xs text-dark-500 uppercase">Flujo Manual</div>

                                  {request.status === 'PENDING' && (
                                    <button
                                      onClick={() => {
                                        setOpenDropdownId(null)
                                        handleApprove(request.id)
                                      }}
                                      disabled={isApproving}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2 text-blue-400 disabled:opacity-50"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                      1. Aprobar
                                    </button>
                                  )}

                                  {request.status === 'APPROVED' && (
                                    <button
                                      onClick={() => {
                                        setOpenDropdownId(null)
                                        setConfirmModalId(request.id)
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2 text-yellow-400"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                      2. Ingresar Hash + Confirmar
                                    </button>
                                  )}

                                  <div className="border-t border-dark-700 my-1" />

                                  {/* Rechazar */}
                                  <button
                                    onClick={() => {
                                      setOpenDropdownId(null)
                                      setRejectModalId(request.id)
                                    }}
                                    disabled={isRejecting}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-dark-700 flex items-center gap-2 text-red-400 disabled:opacity-50"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Rechazar
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                        {request.status === 'SCHEDULED' && (
                          <span className="text-xs text-purple-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Programado
                          </span>
                        )}
                        {request.status === 'COMPLETED' && request.destTxHash && (
                          <a
                            href={getExplorerUrl(56, 'tx', request.destTxHash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-400 hover:underline flex items-center gap-1 text-xs"
                          >
                            BSC TX <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {request.status === 'REJECTED' && (
                          <span className="text-xs text-red-400">Rechazado</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Accordion Row - User Details */}
                  {expandedRowId === request.id && (
                    <tr key={`${request.id}-details`} className="bg-dark-850/50">
                      <td colSpan={12} className="px-4 py-4">
                        {isLoadingDetails ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-uni-500" />
                            <span className="ml-2 text-dark-400">Cargando detalles...</span>
                          </div>
                        ) : userDetails ? (
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {/* Column 1: Plans List */}
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-medium text-white">
                                <Package className="w-4 h-4 text-uni-400" />
                                Planes ({userDetails.plansSummary.totalPlans})
                              </div>
                              {userDetails.plans.length === 0 ? (
                                <p className="text-dark-500 text-xs">Sin planes registrados</p>
                              ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                                  {userDetails.plans.map((plan) => (
                                    <div
                                      key={plan.id}
                                      className={cn(
                                        'p-2 rounded-lg border text-xs',
                                        plan.isClosed
                                          ? 'bg-dark-800/50 border-dark-700'
                                          : plan.isCorporate
                                            ? 'bg-purple-500/10 border-purple-500/30'
                                            : 'bg-accent-500/10 border-accent-500/30'
                                      )}
                                    >
                                      <div className="flex justify-between items-center">
                                        <span className="font-medium">
                                          Plan Nivel {plan.planLevel}
                                          {plan.isCorporate && (
                                            <span className="ml-1 text-purple-400">(Corp)</span>
                                          )}
                                        </span>
                                        <span className={cn(
                                          'badge text-[10px]',
                                          plan.isClosed ? 'bg-dark-600 text-dark-400' : 'bg-green-500/20 text-green-400'
                                        )}>
                                          {plan.isClosed ? 'Cerrado' : 'Activo'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between mt-1 text-dark-400">
                                        <span>${parseInt(plan.amount).toLocaleString()}</span>
                                        <span>{new Date(plan.createdAt).toLocaleDateString('es-ES')}</span>
                                      </div>
                                      {plan.isCorporate && (
                                        <span className={`text-[10px] ${plan.hasPassiveIncome ? 'text-yellow-400' : 'text-dark-500'}`}>
                                          {plan.hasPassiveIncome ? '+ Ingreso Pasivo' : 'Sin Ingreso Pasivo'}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Plans Summary */}
                              <div className="pt-2 border-t border-dark-700 text-xs space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-dark-400">Total Invertido:</span>
                                  <span className="text-white font-medium">${parseInt(userDetails.plansSummary.totalInvested).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-dark-400">Corp. Invertido:</span>
                                  <span className="text-purple-400">${parseInt(userDetails.plansSummary.totalCorporateInvested).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-dark-400">Activos/Cerrados:</span>
                                  <span className="text-dark-300">{userDetails.plansSummary.activePlans}/{userDetails.plansSummary.closedPlans}</span>
                                </div>
                              </div>
                            </div>

                            {/* Column 2: User Balances (MongoDB) */}
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-medium text-white">
                                <DollarSign className="w-4 h-4 text-green-400" />
                                Balances USDT
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="bg-dark-800 rounded-lg p-2">
                                  <span className="text-[10px] text-dark-500 block">Directo</span>
                                  <span className="text-sm font-mono text-green-400">${parseFloat(userDetails.balances.balance_direct || '0').toFixed(2)}</span>
                                </div>
                                <div className="bg-dark-800 rounded-lg p-2">
                                  <span className="text-[10px] text-dark-500 block">Comisiones</span>
                                  <span className="text-sm font-mono text-blue-400">${parseFloat(userDetails.balances.balance_commission || '0').toFixed(2)}</span>
                                </div>
                                <div className="bg-dark-800 rounded-lg p-2">
                                  <span className="text-[10px] text-dark-500 block">Pasivo</span>
                                  <span className="text-sm font-mono text-yellow-400">${parseFloat(userDetails.balances.balance_passive_income || '0').toFixed(2)}</span>
                                </div>
                                <div className="bg-dark-800 rounded-lg p-2">
                                  <span className="text-[10px] text-dark-500 block">Matrix</span>
                                  <span className="text-sm font-mono text-purple-400">${parseFloat(userDetails.balances.matrix_reward || '0').toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="bg-dark-800 rounded-lg p-3 border border-accent-500/30">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs text-dark-400">Balance Recompensas</span>
                                  <span className="text-lg font-mono text-accent-400 font-semibold">
                                    ${(
                                      parseFloat(userDetails.balances.balance_direct || '0') +
                                      parseFloat(userDetails.balances.balance_commission || '0') +
                                      parseFloat(userDetails.balances.balance_passive_income || '0') +
                                      parseFloat(userDetails.balances.matrix_reward || '0')
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Column 3: Token Balances (PostgreSQL) */}
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-medium text-white">
                                <Coins className="w-4 h-4 text-yellow-400" />
                                Tokens (Blockchain)
                              </div>
                              <div className="space-y-2">
                                <div className="bg-dark-800 rounded-lg p-3 border border-uni-500/30">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-uni-400 font-medium">W2USDT</span>
                                    <span className="text-sm font-mono text-white">
                                      {parseFloat(userDetails.tokens.W2USDT.balanceFormatted || '0').toFixed(4)}
                                    </span>
                                  </div>
                                  {userDetails.tokens.W2USDT.lastUpdated && (
                                    <span className="text-[10px] text-dark-500">
                                      Actualizado: {new Date(userDetails.tokens.W2USDT.lastUpdated).toLocaleDateString('es-ES')}
                                    </span>
                                  )}
                                </div>
                                <div className="bg-dark-800 rounded-lg p-3 border border-blue-500/30">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-blue-400 font-medium">UNI2 Token</span>
                                    <span className="text-sm font-mono text-white">
                                      {parseFloat(userDetails.tokens.UNI2_TOKEN.balanceFormatted || '0').toFixed(4)}
                                    </span>
                                  </div>
                                  {userDetails.tokens.UNI2_TOKEN.lastUpdated && (
                                    <span className="text-[10px] text-dark-500">
                                      Actualizado: {new Date(userDetails.tokens.UNI2_TOKEN.lastUpdated).toLocaleDateString('es-ES')}
                                    </span>
                                  )}
                                </div>
                                <div className="bg-dark-800 rounded-lg p-3 border border-green-500/30">
                                  <div className="flex justify-between items-center">
                                    <span className="text-xs text-green-400 font-medium">UNI2 Coin (Gas)</span>
                                    <span className="text-sm font-mono text-white">
                                      {parseFloat(userDetails.tokens.UNI2_COIN.balanceFormatted || '0').toFixed(4)}
                                    </span>
                                  </div>
                                  {userDetails.tokens.UNI2_COIN.lastUpdated && (
                                    <span className="text-[10px] text-dark-500">
                                      Actualizado: {new Date(userDetails.tokens.UNI2_COIN.lastUpdated).toLocaleDateString('es-ES')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center text-dark-500 py-4">
                            No se pudieron cargar los detalles del usuario
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {requests.length === 0 && !isLoading && (
            <div className="p-8 text-center text-dark-400">No se encontraron solicitudes</div>
          )}

          {/* Pagination Controls */}
          {requests.length > 0 && totalPages > 1 && (
            <div className="p-4 border-t border-dark-800 flex items-center justify-between">
              <div className="text-sm text-dark-400">
                Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, requests.length)} de {requests.length}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-dark-300 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Primera
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-dark-300 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1.5 text-sm text-dark-300">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-dark-300 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg bg-dark-800 text-dark-300 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Última
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold">Rechazar Solicitud</h3>
            <p className="text-sm text-dark-400">
              Selecciona una plantilla o escribe una razón personalizada.
            </p>
            {rejectionTemplates.length > 0 && (
              <div>
                <label className="block text-sm text-dark-400 mb-1">Plantilla</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="input text-sm"
                >
                  <option value="">-- Seleccionar plantilla --</option>
                  {rejectionTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.isDefault ? '(Predeterminada)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm text-dark-400 mb-1">Razón del rechazo</label>
              <textarea
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value)
                  setSelectedTemplateId('')
                }}
                placeholder="Razón del rechazo..."
                className="input min-h-[100px] resize-none"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showReasonToUser}
                onChange={(e) => setShowReasonToUser(e.target.checked)}
                className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-accent-500 focus:ring-accent-500"
              />
              <span className="text-sm text-dark-300">Mostrar motivo al usuario</span>
            </label>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setRejectModalId(null)
                  setRejectReason('')
                  setShowReasonToUser(false)
                  const defaultTemplate = rejectionTemplates.find(t => t.isDefault)
                  setSelectedTemplateId(defaultTemplate?.id || '')
                }}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={isRejecting || !rejectReason.trim()}
                className="btn-primary btn-sm bg-red-500 hover:bg-red-600"
              >
                {isRejecting ? 'Rechazando...' : 'Rechazar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold">Rechazar {selectedSummary.pendingCount} Solicitudes</h3>
            <p className="text-sm text-dark-400">
              Selecciona una plantilla o escribe una razón personalizada. Se aplicará a todas las solicitudes.
            </p>
            {rejectionTemplates.length > 0 && (
              <div>
                <label className="block text-sm text-dark-400 mb-1">Plantilla</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => {
                    const templateId = e.target.value
                    setSelectedTemplateId(templateId)
                    if (templateId) {
                      const template = rejectionTemplates.find(t => t.id === templateId)
                      if (template) {
                        setBulkRejectReason(template.content)
                      }
                    }
                  }}
                  className="input text-sm"
                >
                  <option value="">-- Seleccionar plantilla --</option>
                  {rejectionTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.isDefault ? '(Predeterminada)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm text-dark-400 mb-1">Razón del rechazo</label>
              <textarea
                value={bulkRejectReason}
                onChange={(e) => {
                  setBulkRejectReason(e.target.value)
                  setSelectedTemplateId('')
                }}
                placeholder="Razón del rechazo..."
                className="input min-h-[100px] resize-none"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowBulkRejectModal(false)
                  setBulkRejectReason('')
                }}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkReject}
                disabled={isBulkProcessing || !bulkRejectReason.trim()}
                className="btn-primary btn-sm bg-red-500 hover:bg-red-600"
              >
                {isBulkProcessing ? 'Rechazando...' : `Rechazar ${selectedSummary.pendingCount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Release Modal */}
      {confirmModalId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold">Confirmar Envío</h3>
            <p className="text-sm text-dark-400">
              Ingresa el hash de la transacción de BSC donde enviaste los USDT al usuario.
            </p>
            <input
              type="text"
              value={destTxHash}
              onChange={(e) => setDestTxHash(e.target.value)}
              placeholder="0x..."
              className="input font-mono text-sm"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setConfirmModalId(null)
                  setDestTxHash('')
                }}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmRelease}
                disabled={isConfirming || !destTxHash.trim()}
                className="btn-primary btn-sm"
              >
                {isConfirming ? 'Confirmando...' : 'Confirmar Envío'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Add Modal */}
      {showManualAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full space-y-4">
            <h3 className="text-lg font-semibold">Añadir Bridge Manualmente</h3>
            <p className="text-sm text-dark-400">
              Ingresa el hash de la transacción de lock en Uni2 para añadir la solicitud a la cola.
            </p>
            <input
              type="text"
              value={manualTxHash}
              onChange={(e) => setManualTxHash(e.target.value)}
              placeholder="0x..."
              className="input font-mono text-sm"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowManualAddModal(false)
                  setManualTxHash('')
                }}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleManualAdd}
                disabled={isManualAdding || !manualTxHash.trim()}
                className="btn-primary btn-sm"
              >
                {isManualAdding ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Añadir a Cola'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModalRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-lg w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-400" />
                Realizar Pago
              </h3>
              <button
                onClick={closePaymentModal}
                disabled={isExecutingPayment || manualPaymentStep !== 'ready'}
                className="text-dark-400 hover:text-white disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Payment mode tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setPaymentMode('manual')}
                className={cn(
                  'flex-1 p-3 rounded-lg border transition-all flex items-center justify-center gap-2',
                  paymentMode === 'manual'
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
                    : 'bg-dark-800 border-dark-700 text-dark-400 hover:border-dark-600'
                )}
              >
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Manual (Tesorería)</span>
              </button>
              <button
                onClick={() => setPaymentMode('automatic')}
                className={cn(
                  'flex-1 p-3 rounded-lg border transition-all flex items-center justify-center gap-2',
                  paymentMode === 'automatic'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-dark-800 border-dark-700 text-dark-400 hover:border-dark-600'
                )}
              >
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">Automático (Bridge)</span>
              </button>
            </div>

            {/* Request info */}
            <div className="bg-dark-800 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">Destinatario:</span>
                <span className="font-mono">{shortenAddress(paymentModalRequest.toAddress, 8)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">Cantidad a enviar:</span>
                <span className="font-mono text-accent-400">
                  {parseFloat(paymentModalRequest.formattedAmountOut).toFixed(4)} USDT
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">Lock ID:</span>
                <span className="font-mono">{paymentCheck?.request?.lockId || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-dark-400">Estado:</span>
                <span className={cn(
                  'badge text-xs',
                  statusColors[paymentModalRequest.status] || statusColors.PENDING
                )}>
                  {statusLabels[paymentModalRequest.status] || paymentModalRequest.status}
                </span>
              </div>
            </div>

            {/* Loading check */}
            {isCheckingPayment && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
                <span className="ml-2 text-dark-400">Verificando balances...</span>
              </div>
            )}

            {/* MANUAL MODE */}
            {paymentMode === 'manual' && !isCheckingPayment && (
              <div className="space-y-4">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-purple-400 font-medium">Wallet Tesorería</span>
                    <span className="text-xs text-dark-500 font-mono">{shortenAddress(TREASURY_WALLET_BSC, 8)}</span>
                  </div>

                  {!address && (
                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Conecta tu wallet para continuar</span>
                    </div>
                  )}

                  {address && !isConnectedTreasury && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <XCircle className="w-4 h-4" />
                      <span>Conecta la wallet de Tesorería ({shortenAddress(TREASURY_WALLET_BSC, 6)})</span>
                    </div>
                  )}

                  {address && isConnectedTreasury && !isOnBSC && (
                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Cambia a la red BSC para continuar</span>
                    </div>
                  )}

                  {address && isConnectedTreasury && isOnBSC && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Wallet de Tesorería conectada en BSC</span>
                    </div>
                  )}
                </div>

                {/* Manual payment progress */}
                {manualPaymentStep !== 'ready' && (
                  <div className="bg-dark-800 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        manualPaymentStep === 'sending' ? 'bg-yellow-500/20' :
                        manualPaymentStep === 'verifying' || manualPaymentStep === 'completing' ? 'bg-green-500/20' : 'bg-dark-700'
                      )}>
                        {manualPaymentStep === 'sending' ? (
                          <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm">Enviando USDT en BSC</span>
                        {sendUsdtHash && (
                          <a
                            href={getExplorerUrl(56, 'tx', sendUsdtHash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-uni-400 hover:underline flex items-center gap-1"
                          >
                            Ver TX <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-dark-600 mx-auto" />
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        manualPaymentStep === 'completing' ? 'bg-yellow-500/20' : 'bg-dark-700'
                      )}>
                        {manualPaymentStep === 'completing' ? (
                          <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                        ) : (
                          <span className="text-dark-500 text-sm">2</span>
                        )}
                      </div>
                      <span className="text-sm text-dark-400">Completando en contrato UNI2</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AUTOMATIC MODE */}
            {paymentMode === 'automatic' && paymentCheck && !isCheckingPayment && (
              <>
                {/* Balances */}
                {paymentCheck.balances && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-dark-300">Balances Wallet Bridge</h4>

                    {/* BSC Wallet */}
                    <div className="bg-dark-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-yellow-400 font-medium">BSC Wallet</span>
                        <span className="text-xs text-dark-500 font-mono">
                          {shortenAddress(paymentCheck.balances.bsc.address, 6)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-dark-400">USDT:</span>
                          <span className={cn(
                            'font-mono',
                            parseFloat(paymentCheck.balances.bsc.usdtFormatted || '0') >= parseFloat(paymentModalRequest.formattedAmountOut)
                              ? 'text-green-400'
                              : 'text-red-400'
                          )}>
                            {paymentCheck.balances.bsc.usdtFormatted || '0'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-dark-400">BNB:</span>
                          <span className={cn(
                            'font-mono',
                            paymentCheck.balances.bsc.hasEnoughGas ? 'text-green-400' : 'text-red-400'
                          )}>
                            {paymentCheck.balances.bsc.bnbFormatted || '0'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* UNI2 Wallet */}
                    <div className="bg-dark-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-uni-400 font-medium">UNI2 Wallet</span>
                        <span className="text-xs text-dark-500 font-mono">
                          {shortenAddress(paymentCheck.balances.uni2.address, 6)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-dark-400">UNI2 (Gas):</span>
                        <span className={cn(
                          'font-mono',
                          paymentCheck.balances.uni2.hasEnoughGas ? 'text-green-400' : 'text-red-400'
                        )}>
                          {paymentCheck.balances.uni2.nativeFormatted || '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status message */}
                {!paymentCheck.canExecute && paymentCheck.reason && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-sm text-red-400">{paymentCheck.reason}</span>
                  </div>
                )}

                {paymentCheck.canExecute && (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-green-400">Listo para ejecutar el pago</span>
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={closePaymentModal}
                disabled={isExecutingPayment || manualPaymentStep !== 'ready'}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>

              {/* Manual mode actions */}
              {paymentMode === 'manual' && manualPaymentStep === 'ready' && (
                <button
                  onClick={handleManualPayment}
                  disabled={!isConnectedTreasury || isSendingUsdt || isConfirmingUsdt}
                  className="btn-primary btn-sm bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center gap-1"
                >
                  {isSendingUsdt || isConfirmingUsdt ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  Firmar y Enviar USDT
                </button>
              )}

              {/* Automatic mode actions */}
              {paymentMode === 'automatic' && paymentCheck && !isCheckingPayment && (
                <button
                  onClick={handleExecutePayment}
                  disabled={isExecutingPayment || !paymentCheck.canExecute}
                  className="btn-primary btn-sm bg-green-500 hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                >
                  {isExecutingPayment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4" />
                  )}
                  Ejecutar Pago
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Payment Modal */}
      {scheduleModalRequest && (
        <SchedulePaymentModal
          request={scheduleModalRequest}
          onClose={() => setScheduleModalRequest(null)}
          onSuccess={() => refetch()}
        />
      )}

      {/* Bulk Payment Modal */}
      {showBulkPaymentModal && selectedRequests.length > 0 && (
        <BulkPaymentModal
          requests={selectedRequests}
          onClose={() => setShowBulkPaymentModal(false)}
          onSuccess={() => {
            clearSelection()
            refetch()
          }}
        />
      )}

      {/* Alias Modal */}
      {aliasModalRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Tag className="w-5 h-5 text-cyan-400" />
                Alias de Wallet
              </h3>
              <button
                onClick={() => {
                  setAliasModalRequest(null)
                  setAliasValue('')
                }}
                className="text-dark-400 hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-dark-800 rounded-lg p-3 space-y-1">
              <span className="text-xs text-dark-500">Wallet</span>
              <p className="font-mono text-sm text-white break-all">{aliasModalRequest.fromAddress}</p>
              {aliasModalRequest.allMongoUsers && aliasModalRequest.allMongoUsers[0]?.name && (
                <p className="text-xs text-dark-400">Usuario: {aliasModalRequest.allMongoUsers[0].name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-dark-400 mb-1">Alias</label>
              <input
                type="text"
                value={aliasValue}
                onChange={(e) => setAliasValue(e.target.value)}
                placeholder="Ej: Cliente VIP, Tesorería, etc."
                className="input text-sm"
                maxLength={50}
              />
              <p className="text-xs text-dark-500 mt-1">El alias se mostrará en la tabla para identificar rápidamente esta wallet.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setAliasModalRequest(null)
                  setAliasValue('')
                }}
                className="btn-secondary btn-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveAlias}
                disabled={isSavingAlias}
                className="btn-primary btn-sm bg-cyan-500 hover:bg-cyan-600"
              >
                {isSavingAlias ? 'Guardando...' : 'Guardar Alias'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
